/**
 * SetupTOTPPage — Activation de la double authentification (2FA)
 *
 * Wizard en 4 étapes :
 * 1. Introduction — Explication de la 2FA
 * 2. QR Code — Scanner avec une app d'authentification
 * 3. Vérification — Entrer le code pour confirmer
 * 4. Confirmation — Succès + instructions
 *
 * Appelle POST /api/auth/2fa/setup puis POST /api/auth/2fa/enable
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api.js';
import { TOTPInput } from '../components/ui/TOTPInput.js';
import { colors } from '../styles/tokens.js';

/** Données retournées par POST /api/auth/2fa/setup */
interface Setup2FAData {
  qrCode: string;
  secret: string;
  otpauthUrl: string;
}

type Step = 'intro' | 'qrcode' | 'verify' | 'done';

const STEPS: Step[] = ['intro', 'qrcode', 'verify', 'done'];
const STEP_LABELS: Record<Step, string> = {
  intro: 'Introduction',
  qrcode: 'QR Code',
  verify: 'Vérification',
  done: 'Terminé',
};

export function SetupTOTPPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('intro');
  const [setupData, setSetupData] = useState<Setup2FAData | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const currentIndex = STEPS.indexOf(step);

  /** Étape 1 → 2 : Générer le QR code */
  async function handleSetup() {
    setIsLoading(true);
    setError('');

    const res = await apiFetch<Setup2FAData>('/auth/2fa/setup', { method: 'POST' });

    if (res.success && res.data) {
      setSetupData(res.data);
      setStep('qrcode');
    } else {
      setError(res.error ?? 'Impossible de configurer la 2FA');
    }

    setIsLoading(false);
  }

  /** Étape 3 : Vérifier le code et activer */
  async function handleVerify() {
    if (totpCode.length !== 6) return;
    setIsLoading(true);
    setError('');

    const res = await apiFetch<{ message: string }>('/auth/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ totpCode }),
    });

    if (res.success) {
      setStep('done');
    } else {
      setError(res.error ?? 'Code invalide');
      setTotpCode('');
    }

    setIsLoading(false);
  }

  /** Copier le secret dans le presse-papier */
  async function copySecret() {
    if (!setupData?.secret) return;
    await navigator.clipboard.writeText(setupData.secret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Indicateur de progression */}
      <div className="flex items-center justify-between mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center">
            <div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                transition-colors
                ${i <= currentIndex
                  ? 'bg-primary text-accent'
                  : 'bg-gray-200 text-text-light'}
              `}
            >
              {i + 1}
            </div>
            <span
              className={`hidden sm:block ml-2 text-xs font-medium
                ${i <= currentIndex ? 'text-text' : 'text-text-light'}`}
            >
              {STEP_LABELS[s]}
            </span>
            {i < STEPS.length - 1 && (
              <div
                className={`w-8 sm:w-12 h-0.5 mx-2
                  ${i < currentIndex ? 'bg-primary' : 'bg-gray-200'}`}
              />
            )}
          </div>
        ))}
      </div>

      <div className="bg-surface rounded-xl shadow-lg p-6 border border-gray-100">
        {/* Message d'erreur global */}
        {error && (
          <div role="alert" className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ─── Étape 1 : Introduction ─── */}
        {step === 'intro' && (
          <div className="text-center space-y-4">
            <i className="bi bi-shield-lock" style={{ fontSize: 48, color: colors.accent, display: 'block' }} />
            <h1 className="text-xl font-heading font-bold text-accent">
              Double authentification
            </h1>
            <p className="text-text-light text-sm leading-relaxed">
              La double authentification (2FA) ajoute une couche de sécurité
              supplémentaire à votre compte. Après avoir entré votre mot de passe,
              vous devrez saisir un code temporaire généré par une application
              d'authentification sur votre téléphone.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left">
              <p className="text-sm font-medium text-text mb-2">Applications compatibles :</p>
              <ul className="text-sm text-text-light space-y-1">
                <li>Google Authenticator</li>
                <li>Authy</li>
                <li>Microsoft Authenticator</li>
                <li>1Password, Bitwarden, etc.</li>
              </ul>
            </div>
            <button
              onClick={handleSetup}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg
                         bg-primary text-accent font-semibold hover:bg-primary-light
                         transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <i className="bi bi-arrow-repeat animate-spin" style={{ fontSize: 16 }} />
              ) : (
                <i className="bi bi-shield-lock" style={{ fontSize: 16 }} />
              )}
              {isLoading ? 'Configuration...' : 'Activer la 2FA'}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full py-2 text-sm text-text-light hover:text-text transition-colors"
            >
              Annuler
            </button>
          </div>
        )}

        {/* ─── Étape 2 : QR Code ─── */}
        {step === 'qrcode' && setupData && (
          <div className="space-y-5">
            <div className="text-center">
              <i className="bi bi-qr-code" style={{ fontSize: 32, color: colors.accent, display: 'block', marginBottom: 8 }} />
              <h2 className="text-lg font-semibold text-accent">Scannez ce QR code</h2>
              <p className="text-sm text-text-light mt-1">
                Ouvrez votre application d'authentification et scannez le code ci-dessous
              </p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-xl border-2 border-gray-100 inline-block">
                <img
                  src={setupData.qrCode}
                  alt="QR Code pour la configuration 2FA"
                  className="w-48 h-48"
                />
              </div>
            </div>

            {/* Saisie manuelle */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs font-medium text-text-light mb-2">
                Ou entrez manuellement ce code :
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm font-mono bg-white rounded px-3 py-2 border border-gray-200
                                 text-text select-all break-all">
                  {setupData.secret}
                </code>
                <button
                  onClick={copySecret}
                  className="shrink-0 p-2 rounded-lg bg-white border border-gray-200
                             hover:bg-gray-50 transition-colors"
                  aria-label="Copier le code"
                  title="Copier"
                >
                  {secretCopied ? (
                    <i className="bi bi-check-circle" style={{ fontSize: 14, color: '#16a34a' }} />
                  ) : (
                    <i className="bi bi-clipboard" style={{ fontSize: 14, color: colors.textMuted }} />
                  )}
                </button>
              </div>
            </div>

            <button
              onClick={() => setStep('verify')}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg
                         bg-primary text-accent font-semibold hover:bg-primary-light transition-colors"
            >
              Continuer
              <i className="bi bi-arrow-right" style={{ fontSize: 14 }} />
            </button>

            <button
              onClick={() => {
                setStep('intro');
                setSetupData(null);
                setError('');
              }}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm
                         text-text-light hover:text-text transition-colors"
            >
              <i className="bi bi-arrow-left" style={{ fontSize: 12 }} />
              Retour
            </button>
          </div>
        )}

        {/* ─── Étape 3 : Vérification ─── */}
        {step === 'verify' && (
          <div className="space-y-5">
            <div className="text-center">
              <i className="bi bi-shield-lock" style={{ fontSize: 32, color: colors.accent, display: 'block', marginBottom: 8 }} />
              <h2 className="text-lg font-semibold text-accent">Vérifiez le code</h2>
              <p className="text-sm text-text-light mt-1">
                Entrez le code à 6 chiffres affiché dans votre application
              </p>
            </div>

            <div>
              <TOTPInput
                value={totpCode}
                onChange={setTotpCode}
                disabled={isLoading}
                autoFocus
              />
              <p className="text-xs text-text-light text-center mt-3">
                Ce code change toutes les 30 secondes
              </p>
            </div>

            <button
              onClick={handleVerify}
              disabled={isLoading || totpCode.length !== 6}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg
                         bg-primary text-accent font-semibold hover:bg-primary-light
                         transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <i className="bi bi-arrow-repeat animate-spin" style={{ fontSize: 16 }} />
              ) : (
                <i className="bi bi-check-circle" style={{ fontSize: 16 }} />
              )}
              {isLoading ? 'Vérification...' : 'Vérifier le code'}
            </button>

            <button
              onClick={() => {
                setStep('qrcode');
                setTotpCode('');
                setError('');
              }}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm
                         text-text-light hover:text-text transition-colors"
            >
              <i className="bi bi-arrow-left" style={{ fontSize: 12 }} />
              Retour au QR code
            </button>
          </div>
        )}

        {/* ─── Étape 4 : Confirmation ─── */}
        {step === 'done' && (
          <div className="text-center space-y-5">
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <i className="bi bi-check-circle" style={{ fontSize: 32, color: '#16a34a' }} />
            </div>
            <h2 className="text-xl font-heading font-bold text-accent">
              2FA activée avec succès
            </h2>
            <p className="text-sm text-text-light leading-relaxed">
              Votre compte est désormais protégé par la double authentification.
              À chaque connexion, vous devrez entrer un code depuis votre
              application d'authentification.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
              <p className="text-sm font-medium text-amber-800 mb-1">Important</p>
              <p className="text-xs text-amber-700">
                Si vous perdez l'accès à votre application d'authentification,
                contactez un administrateur pour désactiver la 2FA sur votre compte.
              </p>
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-2.5 rounded-lg bg-primary text-accent font-semibold
                         hover:bg-primary-light transition-colors"
            >
              Retour au tableau de bord
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
